import { lighten, darken, transparentize } from './colorModifiers';

export type Scheme = 'light' | 'dark';

// TODO: Figure out how to list all colors
export type Color =
  | 'text'
  | 'bg-0'
  | 'bg-2'
  | 'bg-3'
  | 'primary'
  | 'primaryText'
  | 'gray5'
  | 'gray10'
  | 'gray20'
  | 'gray30'
  | 'gray40'
  | 'gray50'
  | 'textInvert'
  | 'negative'
  | 'warning'
  | 'positive'
  | 'positiveLight'
  | 'negativeLight'
  | 'yellow'
  | 'white'
  | 'pink'
  | 'clinicalGroupText'
  | 'clinicalGroupBg'
  | 'basicGroupText'
  | 'basicGroupBg'
  | 'transparentBlack';

const white = '#FFFFFF';
const black = '#000000';
const blue = '#0069cc';
const yellow = '#EEC430';
const clinicalGroupText = '#83690C';
const clinicalGroupBg = '#F9EFC8';
const teal = '#1c95ba';
const olive = '#2aa788';
const green = '#31aa31';
const basicGroupText = '#376D37';
const basicGroupBg = '#DCF4DC';
const orange = '#df6c20';
const red = '#d53939';
const pink = '#d33162';
const lavender = '#6e60c9';
const purple = '#9d4dcb';
const dark = '#282C30';
const light = '#F8F8F8';
const legacy = blue;
const primary = blue;
const emerald = '#047857';
const transparentBlack = 'rgba(0, 0, 0, 0.35)';
const blueDark = '#2179df';
const blueLight = '#d3e3f9';
const lightestBlue = '#F4F9FF';

const baseColors = {
  blue,
  yellow,
  teal,
  olive,
  green,
  orange,
  red,
  pink,
  lavender,
  purple,
  clinicalGroupText,
  clinicalGroupBg,
  basicGroupText,
  basicGroupBg,
  transparentBlack,
  legacy,
  blueDark,
  blueLight,
  lightestBlue,
};

const modifyColors = (
  colorsObj: { [index: string]: string },
  keyModifier: string,
  modifier: (value: string) => string,
) =>
  Object.entries(colorsObj)
    .map(([key, value]) => [key, modifier(value)])
    .reduce(
      (obj, [key, value]) => ({ ...obj, [`${key}${keyModifier}`]: value }),
      {},
    );

export const darkColors = {
  ...modifyColors(baseColors, '', (value: string) => lighten(0.2, value)),
} as typeof baseColors;

export type ColorProps = {
  [index: string]: string;
};

type SchemeType = {
  dark: ColorProps;
  light: ColorProps;
};

export const schemes: SchemeType = {
  light: {
    dark,
    white,
    black,
    text: dark,
    textInvert: light,

    'bg-0': lighten(0.05, light),
    'bg-1': light,
    'bg-2': darken(0.05, light),
    'bg-3': darken(0.1, light),
    'bg-4': darken(0.15, light),
    gray5: darken(0.05, light),
    gray10: darken(0.1, light),
    gray15: darken(0.15, light),
    gray20: darken(0.2, light),
    gray30: darken(0.3, light),
    gray40: darken(0.4, light),
    gray50: darken(0.5, light),
    gray60: darken(0.6, light),
    gray70: darken(0.7, light),
    gray80: darken(0.8, light),
    gray90: darken(0.9, light),

    primary,
    primaryLight: transparentize(0.75, primary),
    primaryLightest: transparentize(0.9, primary),
    primaryText: darken(0.1, primary),

    emerald,

    negative: red,
    negativeLight: transparentize(0.75, red),
    negativeLightest: transparentize(0.9, red),
    negativeText: darken(0.2, red),

    warning: orange,
    warningLight: transparentize(0.75, orange),
    warningText: darken(0.1, orange),

    positive: green,
    positiveLight: transparentize(0.65, green),
    positiveText: darken(0.1, green),

    ...baseColors,

    // Add [color]Light keys
    ...modifyColors(baseColors, 'Light', (value: string) =>
      transparentize(0.75, value),
    ),

    // Add [color]Text keys
    ...modifyColors(baseColors, 'Text', (value: string) => darken(0.1, value)),

    // Custom update yellow text to be accessible
    yellowText: darken(0.27, yellow),
  },

  dark: {
    dark,
    white,
    black,
    text: light,
    textInvert: dark,

    'bg-0': darken(0.05, dark),
    'bg-1': dark,
    'bg-2': lighten(0.05, dark),
    'bg-3': lighten(0.1, dark),
    'bg-4': lighten(0.15, dark),
    gray5: lighten(0.05, dark),
    gray10: lighten(0.1, dark),
    gray15: lighten(0.15, dark),
    gray20: lighten(0.2, dark),
    gray30: lighten(0.3, dark),
    gray40: lighten(0.4, dark),
    gray50: lighten(0.5, dark),
    gray60: lighten(0.6, dark),
    gray70: lighten(0.7, dark),
    gray80: lighten(0.8, dark),
    gray90: lighten(0.9, dark),

    primary: darkColors.blue,
    primaryText: lighten(0.15, darkColors.blue),
    primaryLight: transparentize(0.55, darkColors.blue),
    primaryLightest: transparentize(0.9, darkColors.blue),

    negative: darkColors.red,
    negativeLight: transparentize(0.45, darkColors.red),
    negativeLightest: transparentize(0.9, darkColors.red),
    negativeText: lighten(0.1, darkColors.red),

    warning: darkColors.orange,
    warningLight: transparentize(0.55, darkColors.orange),
    warningText: darkColors.orange,

    positive: darkColors.green,
    positiveLight: transparentize(0.55, darkColors.green),
    positiveText: darkColors.green,

    ...darkColors,

    // Add light colors, e.g. 'blueLight'
    ...modifyColors(darkColors, 'Light', (value: string) =>
      transparentize(0.55, value),
    ),

    // Add text colors, e.g. 'blueText'
    ...modifyColors(darkColors, 'Text', (value: string) => value),

    blueText: lighten(0.15, darkColors.blue),
    clinicalGroupText,
    clinicalGroupBg,
    basicGroupText,
    basicGroupBg,
  },
};

/**
 *
 */
export function isColor(s: string): s is Color {
  return !!schemes.dark[s] && !!schemes.light[s];
}

/**
 *
 */
export const getColors = (scheme: Scheme = 'light'): ColorProps =>
  schemes[scheme];

/**
 *
 */
export const getColor = (scheme: Scheme = 'light', key = ''): string =>
  schemes[scheme]?.[key] || '';

export default getColors;
